import { useEffect, useMemo, useState } from "react";
import { useEscapeKey } from "../../../hooks/useEscapeKey";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import CloseIcon from "@mui/icons-material/Close";
import InsightsIcon from "@mui/icons-material/Insights";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// ─── types ────────────────────────────────────────────────────────────────────

export interface IFormularioApiMetrics {
  contextFormId: string;
  formType: string;
  client: string;
  businessAreaId: string;
  vacancyId: string;
  recruiterId: string;
  recruiter: string;
  link: string;
  totalRecords: number;
  totalApplications: number;
  steps: number;
  createdAt: string;
  status?: string | null;
}

export interface MetricsData {
  totals: {
    visits: number;
    started: number;
    completed: number;
    conversionRate: number;
    avgTimeMin: number;
    visitsDelta: number;
    completedDelta: number;
  };
  timeline: {
    dates: string[];
    visits: number[];
    starts: number[];
    completed: number[];
  };
  funnel: { label: string; value: number }[];
  device: { label: string; value: number; color: string }[];
  sources: { label: string; value: number }[];
  stepTimes: { label: string; value: number }[];
  abandonment: { label: string; value: number }[];
}

interface MetricasFormularioModalProps {
  formulario: IFormularioApiMetrics;
  onClose: () => void;
}

// ─── color palette ────────────────────────────────────────────────────────────

const COLORS = {
  primary:   "#0065DD",
  primaryBg: "rgba(0,101,221,0.12)",
  violet:    "#7c3aed",
  violetBg:  "rgba(124,58,237,0.12)",
  green:     "#10b981",
  greenBg:   "rgba(16,185,129,0.12)",
  amber:     "#f59e0b",
  amberBg:   "rgba(245,158,11,0.12)",
  pink:      "#ec4899",
  cyan:      "#06b6d4",
  slate:     "#64748b",
};

// ─── seeded mock data ─────────────────────────────────────────────────────────

const seededRandom = (seedStr: string) => {
  let h = 0;
  for (let i = 0; i < seedStr.length; i++) {
    h = ((h << 5) - h) + seedStr.charCodeAt(i);
    h |= 0;
  }
  if (h === 0) h = 1;
  let state = Math.abs(h);
  return () => {
    state = (state * 9301 + 49297) % 233280;
    return state / 233280;
  };
};

export type TimeRange = 7 | 30 | 90;

export const useFormMetrics = (
  formulario: IFormularioApiMetrics,
  daysBack: TimeRange = 30,
): MetricsData => {
  return useMemo(() => {
    const rand = seededRandom((formulario.contextFormId || "fallback") + ":" + daysBack);
    const isDividido = (formulario.steps ?? 1) >= 2;

    const totalApps = formulario.totalApplications ?? 0;
    const dailyApps = Math.max(0.4, totalApps / 30);
    const baseCompletados = Math.max(
      Math.floor(dailyApps * daysBack),
      Math.floor(15 + rand() * 60),
    );
    const baseIniciados = Math.floor(baseCompletados * (1.3 + rand() * 0.35));
    const baseVisitas   = Math.floor(baseIniciados   * (1.7 + rand() * 0.5));

    const dates: string[]    = [];
    const visits: number[]   = [];
    const starts: number[]   = [];
    const completed: number[]= [];
    const now = new Date();

    for (let i = daysBack - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      dates.push(`${dd}/${mm}`);

      const dailyVisits    = Math.max(1, Math.floor((baseVisitas / daysBack) * (0.35 + rand() * 1.6)));
      const dailyStarts    = Math.floor(dailyVisits * (0.40 + rand() * 0.32));
      const dailyCompleted = Math.floor(dailyStarts * (0.55 + rand() * 0.32));
      visits.push(dailyVisits);
      starts.push(dailyStarts);
      completed.push(dailyCompleted);
    }

    const sumVisits    = visits.reduce((s, v) => s + v, 0);
    const sumStarts    = starts.reduce((s, v) => s + v, 0);
    const sumCompleted = completed.reduce((s, v) => s + v, 0);
    const conversionRate = sumVisits > 0 ? sumCompleted / sumVisits : 0;

    // Period-over-period delta (last half vs first half)
    const half = Math.floor(daysBack / 2);
    const firstVisits = visits.slice(0, half).reduce((s, v) => s + v, 0) || 1;
    const lastVisits  = visits.slice(half).reduce((s, v) => s + v, 0);
    const firstComp   = completed.slice(0, half).reduce((s, v) => s + v, 0) || 1;
    const lastComp    = completed.slice(half).reduce((s, v) => s + v, 0);
    const visitsDelta    = (lastVisits - firstVisits) / firstVisits;
    const completedDelta = (lastComp - firstComp) / firstComp;

    const funnel = isDividido
      ? [
          { label: "Visitas",         value: sumVisits },
          { label: "Iniciaron",       value: sumStarts },
          { label: "Completó Paso 1", value: Math.max(sumCompleted, Math.floor(sumStarts * (0.62 + rand() * 0.18))) },
          { label: "Completó Paso 2", value: sumCompleted },
        ]
      : [
          { label: "Visitas",     value: sumVisits },
          { label: "Iniciaron",   value: sumStarts },
          { label: "Completaron", value: sumCompleted },
        ];

    const movilPct   = 0.55 + rand() * 0.18;
    const desktopPct = 0.20 + rand() * 0.12;
    const tabletPct  = Math.max(0.02, 1 - movilPct - desktopPct);
    const device = [
      { label: "Móvil",   value: Math.round(movilPct   * sumVisits), color: COLORS.primary },
      { label: "Escritorio", value: Math.round(desktopPct * sumVisits), color: COLORS.violet  },
      { label: "Tablet",  value: Math.round(tabletPct  * sumVisits), color: COLORS.green   },
    ];

    const sourceLabels  = ["Directo", "LinkedIn", "WhatsApp", "Email", "Indeed", "Otros"];
    const sourceWeights = [0.38, 0.20, 0.14, 0.12, 0.09, 0.07];
    const sources = sourceLabels.map((label, i) => ({
      label,
      value: Math.max(1, Math.round(sourceWeights[i] * sumVisits * (0.8 + rand() * 0.4))),
    }));

    const stepTimes = isDividido
      ? [
          { label: "Paso 1", value: Math.round((1.8 + rand() * 2.4) * 10) / 10 },
          { label: "Paso 2", value: Math.round((1.0 + rand() * 1.8) * 10) / 10 },
        ]
      : [
          { label: "Completar formulario", value: Math.round((2.4 + rand() * 2.6) * 10) / 10 },
        ];
    const avgTimeMin = stepTimes.reduce((s, t) => s + t.value, 0);

    const abandonment = isDividido
      ? [
          { label: "Pantalla inicio",  value: Math.round((0.18 + rand() * 0.12) * 100) },
          { label: "Datos personales", value: Math.round((0.10 + rand() * 0.08) * 100) },
          { label: "Documentos",       value: Math.round((0.08 + rand() * 0.07) * 100) },
          { label: "Paso 2",           value: Math.round((0.05 + rand() * 0.06) * 100) },
        ]
      : [
          { label: "Pantalla inicio",  value: Math.round((0.18 + rand() * 0.10) * 100) },
          { label: "Datos personales", value: Math.round((0.10 + rand() * 0.08) * 100) },
          { label: "Documentos",       value: Math.round((0.06 + rand() * 0.07) * 100) },
        ];

    return {
      totals: {
        visits: sumVisits,
        started: sumStarts,
        completed: sumCompleted,
        conversionRate,
        avgTimeMin,
        visitsDelta,
        completedDelta,
      },
      timeline: { dates, visits, starts, completed },
      funnel,
      device,
      sources,
      stepTimes,
      abandonment,
    };
  }, [formulario.contextFormId, formulario.steps, formulario.totalApplications, daysBack]);
};

// ─── formatting helpers ───────────────────────────────────────────────────────

const fmtNum = (n: number) => n.toLocaleString("es-CL");
const fmtPct = (n: number) => `${(n * 100).toFixed(1)}%`;
const fmtMin = (n: number) => `${n.toFixed(1)} min`;
const fmtDelta = (n: number) => `${n >= 0 ? "+" : ""}${(n * 100).toFixed(1)}%`;

// ─── KPI card ─────────────────────────────────────────────────────────────────

interface KPICardProps {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string;
  delta?: number;
  hint?: string;
  compact?: boolean;
}

export const KPICard = ({
  icon, iconBg, iconColor, label, value, delta, hint, compact,
}: KPICardProps) => {
  const pos = delta !== undefined && delta >= 0;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        padding: compact ? "12px 14px" : "16px 18px",
        display: "flex",
        flexDirection: "column",
        gap: compact ? "6px" : "10px",
        minWidth: 0,
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
        <span
          style={{
            fontFamily: "Roboto, sans-serif",
            fontSize: compact ? "10px" : "10.5px",
            fontWeight: 600,
            color: "#94a3b8",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
          }}
        >
          {label}
        </span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: compact ? "26px" : "30px",
            height: compact ? "26px" : "30px",
            borderRadius: "9px",
            background: iconBg,
            color: iconColor,
            flexShrink: 0,
          }}
        >
          {icon}
        </span>
      </div>
      <div
        style={{
          fontFamily: "Roboto, sans-serif",
          fontSize: compact ? "20px" : "24px",
          fontWeight: 700,
          color: "#0f172a",
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      {(delta !== undefined || hint) && (
        <div style={{ display: "flex", alignItems: "center", gap: "6px", minHeight: "18px" }}>
          {delta !== undefined && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                fontFamily: "Roboto, sans-serif",
                fontSize: "11.5px",
                fontWeight: 600,
                color: pos ? "#047857" : "#b91c1c",
                background: pos ? "rgba(16,185,129,0.10)" : "rgba(239,68,68,0.10)",
                padding: "1px 7px 1px 4px",
                borderRadius: "8px",
              }}
            >
              {pos
                ? <ArrowDropUpIcon style={{ fontSize: 18, marginRight: "-2px" }} />
                : <ArrowDropDownIcon style={{ fontSize: 18, marginRight: "-2px" }} />}
              {fmtDelta(delta)}
            </span>
          )}
          {hint && (
            <span
              style={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "11px",
                color: "#94a3b8",
              }}
            >
              {hint}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

interface MetricsKPIRowProps {
  metrics: MetricsData;
  compact?: boolean;
}

export const MetricsKPIRow = ({ metrics, compact }: MetricsKPIRowProps) => {
  const { totals } = metrics;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fit, minmax(${compact ? "150px" : "180px"}, 1fr))`,
        gap: compact ? "10px" : "14px",
      }}
    >
      <KPICard
        icon={<VisibilityIcon style={{ fontSize: compact ? 16 : 18 }} />}
        iconBg={COLORS.primaryBg}
        iconColor={COLORS.primary}
        label="Visitas"
        value={fmtNum(totals.visits)}
        delta={totals.visitsDelta}
        compact={compact}
      />
      <KPICard
        icon={<PlayCircleOutlineIcon style={{ fontSize: compact ? 16 : 18 }} />}
        iconBg={COLORS.violetBg}
        iconColor={COLORS.violet}
        label="Iniciaron"
        value={fmtNum(totals.started)}
        hint={totals.visits > 0 ? `${((totals.started / totals.visits) * 100).toFixed(1)}% de visitas` : undefined}
        compact={compact}
      />
      <KPICard
        icon={<CheckCircleOutlineIcon style={{ fontSize: compact ? 16 : 18 }} />}
        iconBg={COLORS.greenBg}
        iconColor={COLORS.green}
        label="Completaron"
        value={fmtNum(totals.completed)}
        delta={totals.completedDelta}
        compact={compact}
      />
      <KPICard
        icon={<TrendingUpIcon style={{ fontSize: compact ? 16 : 18 }} />}
        iconBg={COLORS.amberBg}
        iconColor={COLORS.amber}
        label="Conversión"
        value={fmtPct(totals.conversionRate)}
        hint={`Tiempo prom: ${fmtMin(totals.avgTimeMin)}`}
        compact={compact}
      />
    </div>
  );
};

// ─── timeline line chart ──────────────────────────────────────────────────────

interface ChartWrapperProps {
  title: string;
  hint?: string;
  height?: number;
  children: React.ReactNode;
  rightSlot?: React.ReactNode;
}

const ChartWrapper = ({ title, hint, height = 240, children, rightSlot }: ChartWrapperProps) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: "12px",
      padding: "16px 18px",
      boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px", gap: "10px", flexWrap: "wrap" }}>
      <div>
        <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px", fontWeight: 700, color: "#1e293b" }}>
          {title}
        </div>
        {hint && (
          <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>
            {hint}
          </div>
        )}
      </div>
      {rightSlot}
    </div>
    <div style={{ position: "relative", height, minHeight: 0 }}>
      {children}
    </div>
  </div>
);

interface ChartProps {
  metrics: MetricsData;
  height?: number;
}

export const MetricsTimelineChart = ({ metrics, height = 240 }: ChartProps) => {
  const { dates, visits, starts, completed } = metrics.timeline;

  const data = {
    labels: dates,
    datasets: [
      {
        label: "Visitas",
        data: visits,
        borderColor: COLORS.primary,
        backgroundColor: "rgba(0,101,221,0.10)",
        fill: true,
        tension: 0.35,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      {
        label: "Iniciaron",
        data: starts,
        borderColor: COLORS.violet,
        backgroundColor: "transparent",
        fill: false,
        tension: 0.35,
        borderWidth: 2,
        borderDash: [4, 3],
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      {
        label: "Completaron",
        data: completed,
        borderColor: COLORS.green,
        backgroundColor: "rgba(16,185,129,0.18)",
        fill: true,
        tension: 0.35,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 10, boxHeight: 10, usePointStyle: true,
          font: { family: "Roboto, sans-serif", size: 11 },
          color: "#475569",
        },
      },
      tooltip: {
        backgroundColor: "#0f172a",
        titleFont: { family: "Roboto, sans-serif", size: 12 },
        bodyFont:  { family: "Roboto, sans-serif", size: 11 },
        padding: 10, cornerRadius: 8,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: "#94a3b8",
          font: { family: "Roboto, sans-serif", size: 10 },
          maxRotation: 0,
          autoSkipPadding: 10,
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#f1f5f9" },
        ticks: {
          color: "#94a3b8",
          font: { family: "Roboto, sans-serif", size: 10 },
          precision: 0,
        },
      },
    },
  };

  return (
    <ChartWrapper
      title="Postulaciones en el tiempo"
      hint="Visitas, formularios iniciados y completados"
      height={height}
    >
      <Line data={data} options={options} />
    </ChartWrapper>
  );
};

// ─── funnel (horizontal bars) ─────────────────────────────────────────────────

export const MetricsFunnel = ({ metrics, height = 240 }: ChartProps) => {
  const { funnel } = metrics;
  const max = Math.max(...funnel.map((f) => f.value), 1);
  const palette = [COLORS.primary, COLORS.violet, COLORS.amber, COLORS.green];

  return (
    <ChartWrapper
      title="Embudo de conversión"
      hint="Desde la visita hasta el envío"
      height={height}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "10px", justifyContent: "center", height: "100%" }}>
        {funnel.map((f, idx) => {
          const widthPct = (f.value / max) * 100;
          const prev = idx === 0 ? null : funnel[idx - 1];
          const stepRate = prev && prev.value > 0 ? (f.value / prev.value) * 100 : null;
          const color = palette[idx % palette.length];
          return (
            <div key={f.label} style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "Roboto, sans-serif" }}>
                <span style={{ fontSize: "11.5px", fontWeight: 600, color: "#475569" }}>{f.label}</span>
                <span style={{ fontSize: "11.5px", color: "#1e293b", fontWeight: 600 }}>
                  {fmtNum(f.value)}
                  {stepRate !== null && (
                    <span style={{ color: "#94a3b8", fontWeight: 500, marginLeft: "8px", fontSize: "10.5px" }}>
                      {stepRate.toFixed(1)}%
                    </span>
                  )}
                </span>
              </div>
              <div
                style={{
                  height: "10px",
                  background: "#f1f5f9",
                  borderRadius: "999px",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${widthPct}%` }}
                  transition={{ duration: 0.6, ease: "easeOut", delay: idx * 0.08 }}
                  style={{
                    height: "100%",
                    background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`,
                    borderRadius: "999px",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </ChartWrapper>
  );
};

// ─── device doughnut ──────────────────────────────────────────────────────────

export const MetricsDeviceDoughnut = ({ metrics, height = 240 }: ChartProps) => {
  const { device } = metrics;
  const data = {
    labels: device.map((d) => d.label),
    datasets: [
      {
        data: device.map((d) => d.value),
        backgroundColor: device.map((d) => d.color),
        borderColor: "#fff",
        borderWidth: 2,
        hoverOffset: 4,
      },
    ],
  };
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "62%",
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 10, boxHeight: 10, usePointStyle: true,
          font: { family: "Roboto, sans-serif", size: 11 },
          color: "#475569",
        },
      },
      tooltip: {
        backgroundColor: "#0f172a",
        callbacks: {
          label: (ctx: any) => {
            const total = device.reduce((s, d) => s + d.value, 0) || 1;
            const pct = (ctx.parsed / total) * 100;
            return ` ${ctx.label}: ${fmtNum(ctx.parsed)} (${pct.toFixed(1)}%)`;
          },
        },
        titleFont: { family: "Roboto, sans-serif", size: 12 },
        bodyFont:  { family: "Roboto, sans-serif", size: 11 },
        padding: 10, cornerRadius: 8,
      },
    },
  };
  return (
    <ChartWrapper
      title="Dispositivos"
      hint="Origen del tráfico por tipo de dispositivo"
      height={height}
    >
      <Doughnut data={data} options={options} />
    </ChartWrapper>
  );
};

// ─── sources horizontal bar ───────────────────────────────────────────────────

export const MetricsSourcesBar = ({ metrics, height = 240 }: ChartProps) => {
  const { sources } = metrics;
  const sorted = [...sources].sort((a, b) => b.value - a.value);
  const data = {
    labels: sorted.map((s) => s.label),
    datasets: [
      {
        label: "Visitas",
        data: sorted.map((s) => s.value),
        backgroundColor: [COLORS.primary, COLORS.violet, COLORS.green, COLORS.amber, COLORS.cyan, COLORS.pink],
        borderRadius: 6,
        barThickness: 14,
      },
    ],
  };
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y" as const,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#0f172a",
        titleFont: { family: "Roboto, sans-serif", size: 12 },
        bodyFont:  { family: "Roboto, sans-serif", size: 11 },
        padding: 10, cornerRadius: 8,
      },
    },
    scales: {
      x: {
        grid: { color: "#f1f5f9" },
        ticks: { color: "#94a3b8", font: { family: "Roboto, sans-serif", size: 10 }, precision: 0 },
        beginAtZero: true,
      },
      y: {
        grid: { display: false },
        ticks: { color: "#475569", font: { family: "Roboto, sans-serif", size: 11 } },
      },
    },
  };
  return (
    <ChartWrapper
      title="Fuentes de tráfico"
      hint="¿De dónde vienen los postulantes?"
      height={height}
    >
      <Bar data={data} options={options} />
    </ChartWrapper>
  );
};

// ─── step times bar ───────────────────────────────────────────────────────────

export const MetricsStepTimeBar = ({ metrics, height = 240 }: ChartProps) => {
  const { stepTimes } = metrics;
  const data = {
    labels: stepTimes.map((s) => s.label),
    datasets: [
      {
        label: "Minutos",
        data: stepTimes.map((s) => s.value),
        backgroundColor: stepTimes.map((_, i) =>
          i === 0 ? COLORS.primary : i === 1 ? COLORS.violet : COLORS.green,
        ),
        borderRadius: 8,
        barThickness: 36,
      },
    ],
  };
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#0f172a",
        callbacks: { label: (ctx: any) => ` ${ctx.parsed.y.toFixed(1)} min` },
        padding: 10, cornerRadius: 8,
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: "#475569", font: { family: "Roboto, sans-serif", size: 11 } } },
      y: {
        grid: { color: "#f1f5f9" },
        beginAtZero: true,
        ticks: {
          color: "#94a3b8",
          font: { family: "Roboto, sans-serif", size: 10 },
          callback: (val: any) => `${val} min`,
        },
      },
    },
  };
  return (
    <ChartWrapper
      title="Tiempo promedio de completado"
      hint="Cuánto demora el postulante en cada paso"
      height={height}
    >
      <Bar data={data} options={options} />
    </ChartWrapper>
  );
};

// ─── abandonment bar ──────────────────────────────────────────────────────────

export const MetricsAbandonmentBar = ({ metrics, height = 240 }: ChartProps) => {
  const { abandonment } = metrics;
  const data = {
    labels: abandonment.map((a) => a.label),
    datasets: [
      {
        label: "Tasa de abandono",
        data: abandonment.map((a) => a.value),
        backgroundColor: abandonment.map((a) =>
          a.value >= 15 ? "#ef4444" : a.value >= 10 ? COLORS.amber : COLORS.green,
        ),
        borderRadius: 6,
        barThickness: 24,
      },
    ],
  };
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#0f172a",
        callbacks: { label: (ctx: any) => ` ${ctx.parsed.y}% abandono` },
        padding: 10, cornerRadius: 8,
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: "#475569", font: { family: "Roboto, sans-serif", size: 11 } } },
      y: {
        grid: { color: "#f1f5f9" },
        beginAtZero: true,
        max: 30,
        ticks: {
          color: "#94a3b8",
          font: { family: "Roboto, sans-serif", size: 10 },
          callback: (val: any) => `${val}%`,
        },
      },
    },
  };
  return (
    <ChartWrapper
      title="Abandono por sección"
      hint="Porcentaje de postulantes que abandonan en cada sección"
      height={height}
    >
      <Bar data={data} options={options} />
    </ChartWrapper>
  );
};

// ─── demo banner ──────────────────────────────────────────────────────────────

export const DemoDataBanner = ({ compact }: { compact?: boolean }) => (
  <div
    style={{
      display: "flex",
      gap: "10px",
      alignItems: "flex-start",
      padding: compact ? "8px 12px" : "10px 14px",
      borderRadius: "9px",
      background: "rgba(245,158,11,0.08)",
      border: "1px solid rgba(245,158,11,0.25)",
      marginBottom: compact ? "10px" : "16px",
    }}
  >
    <div
      style={{
        width: "16px", height: "16px", borderRadius: "50%",
        background: COLORS.amber, color: "#fff",
        fontSize: "10px", fontWeight: 700,
        fontFamily: "Roboto, sans-serif",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, marginTop: "1px",
      }}
    >
      i
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontFamily: "Roboto, sans-serif", fontSize: compact ? "11.5px" : "12.5px", fontWeight: 600, color: "#92400e" }}>
        Datos de demostración
      </div>
      <div style={{ fontFamily: "Roboto, sans-serif", fontSize: compact ? "10.5px" : "11.5px", color: "#b45309", marginTop: "2px", lineHeight: 1.45 }}>
        Las métricas se generan localmente como ejemplo. Cuando el backend esté disponible, se reemplazarán por los datos reales del formulario.
      </div>
    </div>
  </div>
);

// ─── context info ─────────────────────────────────────────────────────────────

const TIPO_CONFIG: Record<string, { bg: string; color: string; label: string }> = {
  CLIENTE:    { bg: "#ede9fe", color: "#6d28d9", label: "Cliente"    },
  CUENTA:     { bg: "#dbeafe", color: "#1d4ed8", label: "Cuenta"     },
  RECLUTADOR: { bg: "#fef3c7", color: "#b45309", label: "Reclutador" },
};

const ContextChip = ({ label, value }: { label: string; value: string }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
    <span
      style={{
        fontFamily: "Roboto, sans-serif",
        fontSize: "10.5px",
        fontWeight: 600,
        color: "#94a3b8",
        textTransform: "uppercase",
        letterSpacing: "0.07em",
      }}
    >
      {label}
    </span>
    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#1e293b", fontWeight: 500 }}>
      {value || "—"}
    </span>
  </div>
);

// ─── time range selector ──────────────────────────────────────────────────────

const RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: 7,  label: "7 días"  },
  { value: 30, label: "30 días" },
  { value: 90, label: "90 días" },
];

const RangeSelector = ({
  value, onChange,
}: { value: TimeRange; onChange: (v: TimeRange) => void }) => (
  <div
    style={{
      display: "inline-flex",
      background: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: "9px",
      padding: "2px",
    }}
  >
    {RANGE_OPTIONS.map((opt) => {
      const active = opt.value === value;
      return (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            padding: "5px 11px",
            border: "none",
            background: active ? "#fff" : "transparent",
            color: active ? COLORS.primary : "#64748b",
            fontFamily: "Roboto, sans-serif",
            fontSize: "11.5px",
            fontWeight: active ? 600 : 500,
            cursor: "pointer",
            borderRadius: "7px",
            boxShadow: active ? "0 1px 3px rgba(15,23,42,0.08)" : "none",
            transition: "all 0.15s",
          }}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

// ─── full modal ───────────────────────────────────────────────────────────────

const MetricasFormularioModal = ({ formulario, onClose }: MetricasFormularioModalProps) => {
  // Esc cierra el modal.
  useEscapeKey(onClose);
  const [range, setRange] = useState<TimeRange>(30);
  const metrics = useFormMetrics(formulario, range);
  const tipoCfg = TIPO_CONFIG[formulario.formType] ?? {
    bg: "#f1f5f9", color: "#475569", label: formulario.formType || "—",
  };

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const contextChips: { label: string; value: string }[] = [];
  if (formulario.formType === "CLIENTE" || formulario.formType === "CUENTA") {
    contextChips.push({ label: "Cliente", value: formulario.client });
  }
  if (formulario.formType === "CUENTA") {
    contextChips.push({ label: "Área de negocio", value: formulario.businessAreaId });
  }
  if (formulario.formType === "RECLUTADOR") {
    contextChips.push({ label: "Reclutador", value: formulario.recruiter || formulario.recruiterId });
    contextChips.push({ label: "Vacante", value: formulario.vacancyId });
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        backdropFilter: "blur(5px)",
        WebkitBackdropFilter: "blur(5px)",
        zIndex: 1200,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "32px 24px 48px",
        overflowY: "auto",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 18, scale: 0.97 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "1080px",
          borderRadius: "18px",
          overflow: "hidden",
          background: "#fff",
          boxShadow: "0 28px 64px rgba(15,23,42,0.28), 0 0 0 1px rgba(15,23,42,0.07)",
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
            padding: "22px 24px",
            position: "relative",
          }}
        >
          <button
            onClick={onClose}
            style={{
              position: "absolute", top: "14px", right: "14px",
              width: "28px", height: "28px", borderRadius: "50%",
              border: "none", background: "transparent",
              cursor: "pointer", color: "rgba(255,255,255,0.6)",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 0, lineHeight: 1, outline: "none",
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#fff"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "rgba(255,255,255,0.6)"; }}
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <InsightsIcon style={{ fontSize: 22, color: "#7dd3fc" }} />
            <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "16px", fontWeight: 600, color: "#fff" }}>
              Métricas del formulario
            </div>
          </div>
          <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "rgba(255,255,255,0.55)", marginTop: "3px" }}>
            Desempeño, conversión y origen de los postulantes
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: "20px 24px 24px", maxHeight: "78vh", overflowY: "auto", background: "#f8fafc" }}>
          <DemoDataBanner />

          {/* Context + range */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "20px",
              padding: "12px 16px",
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              marginBottom: "16px",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "4px 12px",
                borderRadius: "14px",
                background: tipoCfg.bg,
                color: tipoCfg.color,
                fontSize: "11.5px",
                fontWeight: 600,
                fontFamily: "Roboto, sans-serif",
                whiteSpace: "nowrap",
              }}
            >
              {tipoCfg.label}
            </span>
            {contextChips.map((c) => (
              <ContextChip key={c.label} label={c.label} value={c.value} />
            ))}
            <div style={{ marginLeft: "auto" }}>
              <RangeSelector value={range} onChange={setRange} />
            </div>
          </div>

          {/* KPIs */}
          <div style={{ marginBottom: "16px" }}>
            <MetricsKPIRow metrics={metrics} />
          </div>

          {/* Row 1: Timeline */}
          <div style={{ marginBottom: "14px" }}>
            <MetricsTimelineChart metrics={metrics} height={260} />
          </div>

          {/* Row 2: Funnel + Device */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: "14px",
              marginBottom: "14px",
            }}
          >
            <MetricsFunnel metrics={metrics} height={260} />
            <MetricsDeviceDoughnut metrics={metrics} height={260} />
          </div>

          {/* Row 3: Sources + Step time */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: "14px",
              marginBottom: "14px",
            }}
          >
            <MetricsSourcesBar metrics={metrics} height={260} />
            <MetricsStepTimeBar metrics={metrics} height={260} />
          </div>

          {/* Row 4: Abandonment */}
          <MetricsAbandonmentBar metrics={metrics} height={240} />
        </div>

        {/* ── Footer ── */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "flex-end",
            background: "#fafafa",
          }}
        >
          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            style={{
              padding: "8px 22px",
              borderRadius: "9px",
              border: "1px solid #e2e8f0",
              background: "#fff",
              color: "#475569",
              fontFamily: "Roboto, sans-serif",
              fontSize: "13px",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Cerrar
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MetricasFormularioModal;
